/**
 * Full-screen option list for enum, set, and people profile fields.
 */
import { router } from "expo-router";

import type { SelectOption } from "@/components/ui/enum-select";
import { SelectOptionList } from "@/components/ui/select-option-list";
import { toggleSetSelection } from "@/components/ui/set-select";
import { Text } from "@/components/ui/text";

import { getProfileFieldDescription } from "../config/profileFieldDescriptions";
import { useProfileForm } from "../context/useProfileForm";
import { useParentSelectOptions } from "../lib/getParentSelectOptions";

type FieldSelectionScreenProps = {
    fieldCode: string;
};

export function FieldSelectionScreen({ fieldCode }: FieldSelectionScreenProps) {
    const { person, values, updateCustomField } = useProfileForm();
    const parentOptions = useParentSelectOptions();
    const field = person.custom_fields[fieldCode];

    if (
        !field ||
        (field.field_type !== "set" &&
            field.field_type !== "enum" &&
            field.field_type !== "people")
    ) {
        return (
            <Text variant="muted" className="px-4">
                This field is not available.
            </Text>
        );
    }

    const description = getProfileFieldDescription(fieldCode);
    const rawValue = values.custom_fields[fieldCode];

    if (field.field_type === "set") {
        const options = field.options ?? [];
        const value = Array.isArray(rawValue) ? rawValue : [];

        return (
            <SelectOptionList
                options={options}
                isSelected={(optionId) => value.includes(optionId)}
                multiple
                description={description}
                onOptionPress={(option: SelectOption) => {
                    updateCustomField(
                        fieldCode,
                        toggleSetSelection(
                            options,
                            value,
                            option.id,
                            !value.includes(option.id),
                        ),
                    );
                }}
            />
        );
    }

    const options =
        field.field_type === "people" ? parentOptions : (field.options ?? []);
    const value = typeof rawValue === "number" ? rawValue : null;

    return (
        <SelectOptionList
            options={options}
            description={description}
            isSelected={(optionId) => value === optionId}
            onOptionPress={(option: SelectOption) => {
                if (value === option.id) {
                    // Already selected, do nothing
                    return;
                }
                updateCustomField(fieldCode, option.id);
                router.back();
            }}
        />
    );
}
